/**
 * FlyerHistoryTabs
 *
 * Shared component rendering all three Flyer History sub-tabs for
 * Venue, Artist, Festival, and Promoter detail pages.
 *
 * Props:
 *   entityType        — 'venue' | 'artist' | 'festival' | 'promoter'
 *   entityId          — number, this page's record id
 *   shows             — array from the entity's get* Lambda (used by Show Flyer
 *                       History and Shows Without Flyers sub-tabs)
 *   searchByOptions   — string[], dropdown options for Show Flyer History filter
 *                       e.g. ['Artist','Festival','Promoter'] for Venue
 *   multiEventTabLabel — string, label for sub-tab 2
 *                       e.g. 'Calendar Flyer History' / 'Tour/Schedule Flyer History'
 *   showFlyerHistoryLabel — string (optional, default 'Show Flyer History')
 *
 * Sub-tab layout per entity:
 *   0 — Show Flyer History  (existing per-event flyers via event.flyer_url)
 *   1 — Calendar/Tour/Festival Flyer History  (multi-event images via image_events)
 *   2 — Shows Without Flyers
 *
 * Spec refs: §4.1, §4.2, §4.3, §7.1
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { getIdToken } from '../attendedUtils';
import { ZoomHint, ImageLightbox } from './ZoomableImage';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

export const FLYER_PAGE_SIZE = 50;
export const FLYER_COLS = 5;
const PAGE_SIZE = 25;

const DATE_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Date (Oldest to Most Recent)' },
];

// Sort options for Show Flyer History — differ by entity type
const ARTIST_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Date (Oldest to Most Recent)' },
    { value: 'cap_desc',  label: 'Capacity (Most to Least)' },
    { value: 'cap_asc',   label: 'Capacity (Least to Most)' },
];

const VENUE_SORT_OPTIONS = [
    { value: 'date_desc', label: 'Date (Most Recent to Oldest)' },
    { value: 'date_asc',  label: 'Date (Oldest to Most Recent)' },
    { value: 'att_desc',  label: 'Attendance (Most to Least)' },
    { value: 'att_asc',   label: 'Attendance (Least to Most)' },
];

export const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : '—';

// ── Sub-tab bar ───────────────────────────────────────────────────────────────
function SubTabBar({ tabs, active, onChange }) {
    return (
        <div style={S.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...S.subTabBtn, ...(active === i ? S.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

// ── Flyer pagination (scrolls to top on page change) ─────────────────────────
export function FlyerPagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / FLYER_PAGE_SIZE);
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    return (
        <div style={S.pagination}>
            <button onClick={() => { onPageChange(currentPage - 1); window.scrollTo(0, 0); }} disabled={currentPage === 1} style={{ ...S.pageBtn, ...(currentPage === 1 ? S.pageBtnDisabled : {}) }}>‹ Prev</button>
            {pages.map(p => (
                <button key={p} onClick={() => { onPageChange(p); window.scrollTo(0, 0); }} style={{ ...S.pageBtn, ...(p === currentPage ? S.pageBtnActive : {}) }}>{p}</button>
            ))}
            <button onClick={() => { onPageChange(currentPage + 1); window.scrollTo(0, 0); }} disabled={currentPage === totalPages} style={{ ...S.pageBtn, ...(currentPage === totalPages ? S.pageBtnDisabled : {}) }}>Next ›</button>
        </div>
    );
}

function StandardPagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    return (
        <div style={S.pagination}>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...S.pageBtn, ...(currentPage === 1 ? S.pageBtnDisabled : {}) }}>‹ Prev</button>
            {pages.map(p => (
                <button key={p} onClick={() => onPageChange(p)} style={{ ...S.pageBtn, ...(p === currentPage ? S.pageBtnActive : {}) }}>{p}</button>
            ))}
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...S.pageBtn, ...(currentPage === totalPages ? S.pageBtnDisabled : {}) }}>Next ›</button>
        </div>
    );
}

// ── FlyerCard — single event flyer thumbnail (Show Flyer History) ─────────────
// Clicking the flyer opens a zoom lightbox rather than navigating directly;
// "View Show" inside the lightbox is the way to reach the event page.
function FlyerCard({ show }) {
    const [hovered, setHovered] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const date = formatDate(show.event_date);
    const tooltip = [date, show.headliners, show.stage_name].filter(Boolean).join(' · ');
    const eventPath = `/events/${show.event_slug || show.event_id}`;
    return (
        <>
            <div
                style={{ ...S.flyerCell, cursor: imgError ? 'default' : 'zoom-in' }}
                title={tooltip}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => { if (!imgError) setLightboxOpen(true); }}
            >
                {imgError ? (
                    <div style={S.flyerErrorBox}>
                        <div style={S.flyerErrorIcon}>🎟️</div>
                        <div style={S.flyerErrorDate}>{date}</div>
                        <div style={S.flyerErrorVenue}>{show.headliners || ''}</div>
                    </div>
                ) : (
                    <img
                        src={show.flyer_url}
                        alt={tooltip}
                        style={{ ...S.flyerImg, opacity: hovered ? 0.85 : 1 }}
                        loading="lazy"
                        onError={() => setImgError(true)}
                    />
                )}
                {!imgError && (
                    <div style={{ ...S.flyerOverlay, opacity: hovered ? 1 : 0 }}>
                        <div style={S.flyerDate}>{date}</div>
                    </div>
                )}
                {!imgError && <ZoomHint visible={hovered} />}
            </div>
            {lightboxOpen && (
                <ImageLightbox
                    src={show.flyer_url}
                    alt={tooltip}
                    caption={tooltip}
                    viewShowTo={eventPath}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </>
    );
}

// ── MultiEventFlyerCard — images from image_events (Calendar/Tour/Festival Flyer History) ──
// viewFestivalTo/festivalLabel are optional — used when this card is shown in
// a context (like a festival series' Flyers tab) where the image is already
// scoped to one specific festival, so a direct link back makes sense.
export function MultiEventFlyerCard({ image, viewFestivalTo, festivalLabel }) {
    const [hovered, setHovered] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // Date range label: earliest to latest linked event date
    const dates = (image.linked_events || []).map(e => e.event_date).filter(Boolean).sort();
    const earliest = dates[0] ? formatDate(dates[0]) : null;
    const latest = dates[dates.length - 1] ? formatDate(dates[dates.length - 1]) : null;
    const dateLabel = earliest && latest && earliest !== latest
        ? `${earliest} – ${latest}`
        : earliest || '';

    const eventCount = image.linked_events?.length || 0;
    const tooltip = [festivalLabel, dateLabel, image.caption, `${eventCount} show${eventCount !== 1 ? 's' : ''}`].filter(Boolean).join(' · ');

    return (
        <>
            <div
                style={{ ...S.flyerCell, cursor: imgError ? 'default' : 'zoom-in' }}
                title={tooltip}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => { if (!imgError) setLightboxOpen(true); }}
            >
                {imgError ? (
                    <div style={S.flyerErrorBox}>
                        <div style={S.flyerErrorIcon}>🖼️</div>
                        <div style={S.flyerErrorDate}>{dateLabel}</div>
                        <div style={S.flyerErrorVenue}>{festivalLabel || image.caption || ''}</div>
                    </div>
                ) : (
                    <img
                        src={image.image_url}
                        alt={tooltip}
                        style={{ ...S.flyerImg, opacity: hovered ? 0.85 : 1 }}
                        loading="lazy"
                        onError={() => setImgError(true)}
                    />
                )}
                {!imgError && (
                    <div style={{ ...S.flyerOverlay, opacity: hovered ? 1 : 0 }}>
                        {festivalLabel && (
                            <div style={{ fontSize: '11px', color: '#fff', fontWeight: '600', textAlign: 'center' }}>{festivalLabel}</div>
                        )}
                        <div style={S.flyerDate}>{dateLabel}</div>
                        {eventCount > 0 && (
                            <div style={{ fontSize: '9px', color: '#ccc', textAlign: 'center', marginTop: '2px' }}>
                                {eventCount} show{eventCount !== 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                )}
                {!imgError && <ZoomHint visible={hovered} />}
            </div>
            {lightboxOpen && (
                <ImageLightbox
                    src={image.image_url}
                    alt={tooltip}
                    caption={[festivalLabel, dateLabel, image.caption].filter(Boolean).join(' · ')}
                    viewShowTo={viewFestivalTo}
                    viewLinkLabel="View Festival →"
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </>
    );
}

// ── Sub-tab 0: Show Flyer History ─────────────────────────────────────────────
// Wraps the existing per-event flyer grid. searchByOptions drives the filter
// dropdown; all filtering is client-side against the shows array from the Lambda.
function ShowFlyerHistoryTab({ shows, searchByOptions, entityType }) {
    const isMobile = useIsMobile();
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState('date_desc');
    const [filterType, setFilterType] = useState(searchByOptions[0]?.toLowerCase() || 'artist');
    const [filterSearch, setFilterSearch] = useState('');

    const sortOptions = entityType === 'venue' ? VENUE_SORT_OPTIONS : ARTIST_SORT_OPTIONS;

    const flyers = shows ? shows.filter(s => s.flyer_url) : [];

    if (flyers.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎟️</div>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Flyers Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Event flyers will appear here as they are added.</div>
            </div>
        );
    }

    const q = normalizeStr(filterSearch.trim());
    const filtered = q ? flyers.filter(s => {
        switch (filterType) {
            case 'artist':   return (s.headliners && normalizeStr(s.headliners).includes(q)) || (s.support_acts && normalizeStr(s.support_acts).includes(q)) || (s.other_artists && normalizeStr(s.other_artists).includes(q)) || (s.artists && normalizeStr(s.artists).includes(q));
            case 'festival': return s.festival_name && normalizeStr(s.festival_name).includes(q);
            case 'promoter': return s.promoters && normalizeStr(s.promoters).includes(q);
            case 'venue':    return (s.venue_name && normalizeStr(s.venue_name).includes(q)) || (s.city && normalizeStr(s.city).includes(q)) || (s.venue_city && normalizeStr(s.venue_city).includes(q));
            default:         return true;
        }
    }) : flyers;

    const sorted = [...filtered].sort((a, b) => {
        if (sortKey === 'date_desc') return new Date(b.event_date) - new Date(a.event_date);
        if (sortKey === 'date_asc')  return new Date(a.event_date) - new Date(b.event_date);
        if (sortKey === 'att_desc') {
            const aV = a.estimated_attendance != null ? parseInt(a.estimated_attendance) : -1;
            const bV = b.estimated_attendance != null ? parseInt(b.estimated_attendance) : -1;
            return bV - aV;
        }
        if (sortKey === 'att_asc') {
            const aV = a.estimated_attendance != null ? parseInt(a.estimated_attendance) : Infinity;
            const bV = b.estimated_attendance != null ? parseInt(b.estimated_attendance) : Infinity;
            return aV - bV;
        }
        if (sortKey === 'cap_desc') {
            const aV = a.capacity != null ? parseInt(a.capacity) : -1;
            const bV = b.capacity != null ? parseInt(b.capacity) : -1;
            return bV - aV;
        }
        if (sortKey === 'cap_asc') {
            const aV = a.capacity != null ? parseInt(a.capacity) : Infinity;
            const bV = b.capacity != null ? parseInt(b.capacity) : Infinity;
            return aV - bV;
        }
        return 0;
    });

    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const remainder = paginated.length % FLYER_COLS;
    const padCount  = remainder === 0 ? 0 : FLYER_COLS - remainder;
    const placeholder = `Search ${filterType}...`;

    return (
        <div>
            <div style={S.flyerControlsBar}>
                <div style={S.flyerFilterGroup}>
                    <select
                        value={filterType}
                        onChange={e => { setFilterType(e.target.value); setFilterSearch(''); setPage(1); }}
                        style={S.flyerFilterTypeSelect}
                    >
                        {searchByOptions.map(opt => (
                            <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                        ))}
                    </select>
                    <input
                        type="text"
                        value={filterSearch}
                        onChange={e => { setFilterSearch(e.target.value); setPage(1); }}
                        placeholder={placeholder}
                        style={S.flyerFilterInput}
                    />
                    {filterSearch && (
                        <button onClick={() => { setFilterSearch(''); setPage(1); }} style={S.flyerFilterClear} title="Clear filter">✕</button>
                    )}
                </div>
                <div style={S.flyerSortGroup}>
                    <label style={S.flyerSortLabel}>Sort by</label>
                    <select value={sortKey} onChange={e => { setSortKey(e.target.value); setPage(1); }} style={S.flyerSortSelect}>
                        {sortOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {sorted.length === 0 ? (
                <div style={S.emptyTab}>No flyers match your filter.</div>
            ) : (
                <>
                    <div style={isMobile ? { ...S.flyerGrid, ...Smobile.flyerGrid } : S.flyerGrid}>
                        {paginated.map(show => <FlyerCard key={show.event_id} show={show} />)}
                        {Array.from({ length: padCount }).map((_, i) => <div key={`pad-${i}`} style={S.flyerCellPad} />)}
                    </div>
                    <FlyerPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
                    <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                        {q ? `${sorted.length} of ${flyers.length} flyer${flyers.length !== 1 ? 's' : ''}` : `${flyers.length} flyer${flyers.length !== 1 ? 's' : ''}`}
                        {Math.ceil(sorted.length / FLYER_PAGE_SIZE) > 1 ? ` · Page ${page} of ${Math.ceil(sorted.length / FLYER_PAGE_SIZE)}` : ''}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Sub-tab 1: Calendar / Tour-Schedule / Festival Flyer History ──────────────
// Fetches multi-event images via getImages?mode=flyer-calendar.
// tabLabel is passed in as multiEventTabLabel (used by the parent SubTabBar,
// not rendered here, but the component is named generically).
function MultiEventFlyerHistoryTab({ entityType, entityId }) {
    const isMobile = useIsMobile();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);

    const load = useCallback(async () => {
        if (!entityId) return;
        setLoading(true);
        setError('');
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType, entityId, mode: 'flyer-calendar', sort: sortDir },
                headers: { Authorization: token },
            });
            setImages(res.data.images || []);
        } catch {
            setError('Failed to load flyer images.');
        } finally {
            setLoading(false);
        }
    }, [entityType, entityId, sortDir]);

    useEffect(() => { load(); }, [load]);

    const paginated = images.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const remainder = paginated.length % FLYER_COLS;
    const padCount  = remainder === 0 ? 0 : FLYER_COLS - remainder;

    if (loading) return <div style={S.emptyTab}>Loading…</div>;
    if (error)   return <div style={S.emptyTab}>{error}</div>;

    if (images.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🖼️</div>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Multi-Event Flyers Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Tour flyers and calendar images will appear here once uploaded.</div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={S.flyerSortGroup}>
                    <label style={S.flyerSortLabel}>Sort by</label>
                    <select
                        value={sortDir}
                        onChange={e => { setSortDir(e.target.value); setPage(1); }}
                        style={S.flyerSortSelect}
                    >
                        {DATE_SORT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value === 'date_desc' ? 'desc' : 'asc'}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div style={isMobile ? { ...S.flyerGrid, ...Smobile.flyerGrid } : S.flyerGrid}>
                {paginated.map(img => <MultiEventFlyerCard key={img.id} image={img} />)}
                {Array.from({ length: padCount }).map((_, i) => <div key={`pad-${i}`} style={S.flyerCellPad} />)}
            </div>
            <FlyerPagination currentPage={page} totalItems={images.length} onPageChange={p => { setPage(p); }} />
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                {images.length} image{images.length !== 1 ? 's' : ''}
                {Math.ceil(images.length / FLYER_PAGE_SIZE) > 1 ? ` · Page ${page} of ${Math.ceil(images.length / FLYER_PAGE_SIZE)}` : ''}
            </div>
        </div>
    );
}

// ── Sub-tab 2: Shows Without Flyers ──────────────────────────────────────────
// Columns differ by entity type, per spec §3.4 / existing page conventions:
//   venue   — Date, Stage, Lineup           (stage is meaningful; city is fixed)
//   artist  — Date, Venue, Location, Lineup (artist plays many venues)
//   festival — Date, Venue, Location, Lineup
//   promoter — Date, Venue, Location, Lineup
function ShowsWithoutFlyersTab({ shows, entityType }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const noFlyer = shows ? shows.filter(s => !s.flyer_url) : [];

    if (noFlyer.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎉</div>
                <div style={{ color: '#888', fontSize: '15px' }}>All shows have a flyer — nothing missing here.</div>
            </div>
        );
    }

    const isVenue = entityType === 'venue';
    const q = normalizeStr(search);

    // Helper: normalize location fields — some entities (Promoter) use
    // venue_city/venue_state/venue_country while others (Artist) use city/state/country
    const getCity    = s => s.city    || s.venue_city    || null;
    const getState   = s => s.state   || s.venue_state   || null;
    const getCountry = s => s.country || s.venue_country || null;
    const getVenueName = s => s.venue_name || null;
    const getVenueSlug = s => s.venue_slug || null;
    const getVenueId   = s => s.venue_id   || null;
    const getArtists   = s => s.headliners || s.other_artists || s.artists || null;
    const getSupportActs = s => s.support_acts || null;

    const filtered = search ? noFlyer.filter(s => {
        const dateStr = s.event_date ? formatDate(s.event_date) : '';
        const base = [dateStr, getArtists(s), getSupportActs(s), s.festival_name];
        const extra = isVenue
            ? [s.stage_name]
            : [getVenueName(s), getCity(s), getState(s), getCountry(s)];
        return [...base, ...extra].some(v => v && normalizeStr(v).includes(q));
    }) : noFlyer;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const placeholder = isVenue
        ? 'Search by date, artists, stage, festival...'
        : 'Search by date, venue, city, state, country, artists...';

    return (
        <div>
            <input
                type="text"
                placeholder={placeholder}
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={S.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={S.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={S.tableContainer}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    <th style={S.th}>Date</th>
                                    {isVenue ? (
                                        <th style={S.th}>Stage</th>
                                    ) : (
                                        <>
                                            <th style={S.th}>Venue</th>
                                            <th style={S.th}>Location</th>
                                        </>
                                    )}
                                    <th style={S.th}>Lineup</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((s, idx) => {
                                    const date = formatDate(s.event_date);
                                    const location = isVenue ? null : [getCity(s), getState(s), getCountry(s)].filter(Boolean).join(', ');
                                    const artists = getArtists(s);
                                    const supportActs = getSupportActs(s);
                                    const lineup = s.festival_name && s.festival_slug
                                        ? <><Link to={`/festivals/${s.festival_slug}`} style={S.tableLink}>{s.festival_name}</Link>{artists ? ` — ${artists}` : ''}</>
                                        : artists
                                            ? <>{artists}{supportActs && <span style={{ color: '#666', fontSize: '12px' }}> · {supportActs}</span>}</>
                                            : '—';
                                    return (
                                        <tr key={s.event_id} style={idx % 2 === 0 ? S.trEven : S.trOdd}>
                                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                                                <Link to={`/events/${s.event_slug || s.event_id}`} style={S.tableLink}>{date}</Link>
                                            </td>
                                            {isVenue ? (
                                                <td style={{ ...S.td, color: '#aaa', fontSize: '13px' }}>{s.stage_name || '—'}</td>
                                            ) : (
                                                <>
                                                    <td style={S.td}>
                                                        {getVenueSlug(s) || getVenueId(s)
                                                            ? <Link to={`/venues/${getVenueSlug(s) || getVenueId(s)}`} style={S.tableLink}>{getVenueName(s) || '—'}</Link>
                                                            : getVenueName(s) || '—'}
                                                    </td>
                                                    <td style={{ ...S.td, color: '#aaa' }}>{location || '—'}</td>
                                                </>
                                            )}
                                            <td style={{ ...S.td, color: '#aaa', fontSize: '13px' }}>{lineup}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555', textAlign: 'right' }}>
                        {filtered.length} show{filtered.length !== 1 ? 's' : ''} without a flyer
                    </div>
                    <StandardPagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function FlyerHistoryTabs({
    entityType,
    entityId,
    shows,
    searchByOptions,
    multiEventTabLabel,
    showFlyerHistoryLabel = 'Show Flyer History',
}) {
    const [activeSubTab, setActiveSubTab] = useState(0);

    const subTabs = [showFlyerHistoryLabel, multiEventTabLabel, 'Shows Without Flyers'];

    return (
        <div>
            <SubTabBar tabs={subTabs} active={activeSubTab} onChange={setActiveSubTab} />
            <div style={S.subTabContent}>
                {activeSubTab === 0 && (
                    <ShowFlyerHistoryTab
                        shows={shows}
                        searchByOptions={searchByOptions}
                        entityType={entityType}
                    />
                )}
                {activeSubTab === 1 && (
                    <MultiEventFlyerHistoryTab
                        entityType={entityType}
                        entityId={entityId}
                    />
                )}
                {activeSubTab === 2 && (
                    <ShowsWithoutFlyersTab
                        shows={shows}
                        entityType={entityType}
                    />
                )}
            </div>
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
// Mirrors the style values from VenueDetail.js exactly so the component
// renders identically to the existing Flyer History on that page.
export const S = {
    // Sub-tab bar
    // overflowX/minWidth: found via a follow-up report (not part of the
    // original Phase 2 audit) — this bar had zero overflow handling at all,
    // unlike every other tab bar in the codebase, so 3 tab labels forced
    // the whole page wider than the viewport on mobile with no scroll
    // fallback. subTabBtn already has whiteSpace: 'nowrap' set correctly.
    subTabBar: { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #333', paddingBottom: '0', overflowX: 'auto', minWidth: 0 },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px', padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: '#888', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    subTabContent: { paddingTop: '16px' },

    // Flyer grid
    flyerControlsBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
    flyerFilterGroup: { display: 'flex', alignItems: 'center', gap: '6px', flex: '0 1 auto' },
    flyerFilterTypeSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', cursor: 'pointer', flexShrink: 0, width: '110px' },
    flyerFilterInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', width: '260px', outline: 'none' },
    flyerFilterClear: { background: 'transparent', border: 'none', color: '#666', fontSize: '14px', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 },
    flyerSortGroup: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    flyerSortLabel: { fontSize: '13px', color: '#888', whiteSpace: 'nowrap' },
    flyerSortSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', color: '#e0e0e0', cursor: 'pointer' },
    flyerGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '8px' },
    flyerCell: { position: 'relative', display: 'block', aspectRatio: '8.5 / 11', overflow: 'hidden', borderRadius: '4px', border: '1px solid #2e2e2e', backgroundColor: '#111', textDecoration: 'none' },
    flyerCellPad: { aspectRatio: '8.5 / 11', visibility: 'hidden' },
    flyerImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block', transition: 'opacity 0.15s ease' },
    flyerOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', padding: '20px 6px 5px', transition: 'opacity 0.15s ease' },
    flyerDate: { fontSize: '10px', color: '#e0e0e0', fontWeight: '600', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    flyerErrorBox: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px', backgroundColor: '#1a1a1a' },
    flyerErrorIcon: { fontSize: '20px', opacity: 0.4 },
    flyerErrorDate: { fontSize: '10px', color: '#666', textAlign: 'center' },
    flyerErrorVenue: { fontSize: '10px', color: '#555', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' },

    // Table (Shows Without Flyers)
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },

    // Pagination
    pagination: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' },
    pageBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', color: '#aaa', cursor: 'pointer' },
    pageBtnActive: { backgroundColor: '#bb86fc', borderColor: '#bb86fc', color: '#121212', fontWeight: 'bold' },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.2) ──
// Kept as a separate object from `S`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
//
// NOTE on FLYER_COLS: the pad-cell math above (remainder/padCount) still
// pads each page's item count to a multiple of 5 (FLYER_COLS), not 2, even
// though the grid itself renders 2 columns on mobile. The pad cells are
// invisible (visibility: 'hidden') placeholders, so the only visible effect
// is that the grid can occasionally end with one extra blank row on mobile.
// Making FLYER_COLS itself mobile-aware would also require changes in
// FestivalDetail.js, which imports and reuses it directly — treated as
// out of scope for this pass since the visual impact is a harmless blank
// row, not broken or overlapping content.
const Smobile = {
    flyerGrid: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
};

export { Smobile };
